@let overallLoading = isOverallLoading();

<div class="flex flex-col gap-4">
  <p class="text-sm text-shikenso-purple-base-60 mb-2" data-testid="campaign-details-kpis-description">
    {{ 'pages.campaignDetails.kpis.description' | translate }}
  </p>

  @if (overallLoading) {
    <ngx-skeleton-loader
      class="w-[252px] h-[30px] block"
      [theme]="{ height: '30px', width: '252px', 'border-radius': '100vw' }"
    />
  } @else {
    <app-campaign-details-deliverables-filter-select
      class="w-[252px] mb-2"
      [emptyLabel]="kpiFilterEmptyLabel()"
      [loading]="false"
      [options]="kpiFilterOptions()"
      [searchPlaceholder]="'pages.campaignDetails.kpis.filters.searchKpi' | translate"
      [selectedValues]="selectedKpiKeys()"
      (selectionChanged)="onKpiSelectionChanged($event)"
    />
  }

  @if (overallLoading) {
    <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
      @for (i of [1, 2, 3, 4, 5, 6]; track i) {
        <ngx-skeleton-loader class="h-[220px]" [theme]="{ height: '220px', width: '100%', 'border-radius': '16px' }" />
      }
    </div>
  } @else {
    @let cards = orderedKpiCards();

    @if (cards.length > 0) {
      <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        @for (card of cards; track card.analyticsKey) {
          @let pinned = isPinned(card.analyticsKey);
          @let expanded = isCardExpanded(card.analyticsKey);

          <div class="flex flex-col bg-shikenso-gray-base-24 rounded-2xl h-fit overflow-hidden">
            <!-- Card header (clickable to expand/collapse) -->
            <div
              class="flex items-center justify-between px-4 pt-4 pb-4 cursor-pointer"
              (click)="toggleCard(card.analyticsKey)"
            >
              <div class="flex items-center gap-2">
                <app-icon [src]="card.icon" [size]="16" alt="kpi icon" class="w-4 h-4 flex-shrink-0" />
                <span class="text-base font-bold">
                  {{ getKpiTranslationKey(card.analyticsKey) | translate }}
                </span>
              </div>

              <div class="flex items-center gap-1">
                <button
                  mat-icon-button
                  class="!w-8 !h-8 flex items-center justify-center"
                  matTooltipPosition="above"
                  [matTooltipShowDelay]="400"
                  [matTooltip]="
                    (pinned ? 'pages.campaignDetails.kpis.unpin' : 'pages.campaignDetails.kpis.pin') | translate
                  "
                  (click)="togglePin(card.analyticsKey, $event)"
                >
                  @if (pinned) {
                    <app-icon src="icons/pin-filled.svg" [size]="16" alt="pinned" class="w-4 h-4" />
                  } @else {
                    <app-icon src="icons/pin.svg" [size]="16" alt="pin" class="w-4 h-4" />
                  }
                </button>

                <app-icon
                  src="icons/chevron-down.svg"
                  alt="expand"
                  class="w-4 h-4 flex-shrink-0 transition-transform duration-200"
                  [class.rotate-180]="expanded"
                  [size]="16"
                />
              </div>
            </div>

            <div [@expandCollapse]="expanded ? 'expanded' : 'collapsed'" class="overflow-hidden">
              <hr class="w-full border-shikenso-purple-base-12" />

              <div class="px-4 pb-4">
                <!-- Table header -->
                <div class="grid gap-2 py-3" [style.grid-template-columns]="card.gridTemplate">
                  <span class="text-sm font-bold text-shikenso-purple-base-60">
                    {{ 'pages.campaignDetails.kpis.table.platform' | translate }}
                  </span>

                  @for (column of card.columns; track column) {
                    <div class="flex gap-1 items-center justify-end">
                      <span class="text-sm font-bold text-shikenso-purple-base-60 text-right">
                        {{ getColumnTranslationKey(column) | translate }}
                      </span>
                      <app-icon
                        src="icons/info-circle.svg"
                        alt="info"
                        color="var(--shikenso-purple-base-60)"
                        matTooltipPosition="above"
                        [matTooltip]="getColumnTooltipTranslationKey(column, card.analyticsKey) | translate"
                        [size]="14"
                        [appTrackInfoHover]="column"
                        [currentTab]="currentTab"
                      />
                    </div>
                  }
                </div>

                @let baselineLoading = store.baselineLoading$();

                <!-- Platform rows -->
                @for (row of card.platformRows; track row.platformFeature) {
                  <div class="grid gap-2 py-1" [style.grid-template-columns]="card.gridTemplate">
                    <!-- Platform column -->
                    <div class="flex items-center gap-2 min-w-0">
                      <app-social-badge
                        backgroundColor="transparent"
                        [type]="row.platformFeature"
                        [size]="16"
                        [iconSize]="16"
                        [color]="row.platformColor"
                      />

                      <span
                        matTooltip
                        matTooltipPosition="left"
                        class="text-sm line-clamp-1 text-ellipsis"
                        [appTextClampTooltip]="'shared.platformFeatures.' + row.platformFeature | translate"
                        [style.color]="row.platformColor"
                      >
                        {{ 'shared.platformFeatures.' + row.platformFeature | translate }}
                      </span>
                    </div>

                    @for (column of card.columns; track column) {
                      <div class="text-sm text-right font-bold">
                        @if (column === 'base' && baselineLoading) {
                          <ngx-skeleton-loader
                            class="w-10 h-4 inline-block"
                            [theme]="{ height: '16px', width: '40px', 'border-radius': '4px', display: 'inline-block' }"
                          />
                        } @else {
                          @let columnValue = getRowColumnValue(row, column);

                          @if (columnValue !== null) {
                            @if (card.isPercentage) {
                              <span matTooltipPosition="above" [matTooltip]="columnValue | numberLocale: true">
                                {{ columnValue | percentage: true : 1 }}%
                              </span>
                            } @else {
                              <span matTooltipPosition="above" [matTooltip]="columnValue | numberLocale">
                                {{ columnValue | numberSuffix }}
                              </span>
                            }
                          } @else {
                            <span class="text-shikenso-purple-base-40">-</span>
                          }
                        }
                      </div>
                    }
                  </div>
                }
              </div>
            </div>
          </div>
        }
      </div>
    } @else {
      <div class="flex items-center justify-center w-full py-12">
        <span class="text-lg font-medium">{{ 'shared.noKpis' | translate }}</span>
      </div>
    }
  }
</div>
